import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, ChevronRight, Home } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';
import { useTour } from '../../context/TourContext';
import api, { notificationsAPI } from '../../services/api';
import type { Notification } from '../../types';
import { formatNotificationDateTime } from '../../utils/formatting';
import { getHeaderBreadcrumbs } from './getHeaderBreadcrumbs';
import HeaderNotifications from './HeaderNotifications';
import HeaderUserMenu from './HeaderUserMenu';

interface HeaderProps {
  setIsOpen: (isOpen: boolean) => void;
}

const Header: React.FC<HeaderProps> = ({ setIsOpen }) => {
  const { user, logout, loading: authLoading } = useAuth();
  const { startTour, run: isTourRunning } = useTour();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [runName, setRunName] = useState<string | null>(null);
  const showNotificationsRef = useRef(showNotifications);
  showNotificationsRef.current = showNotifications;
  const notificationsContainerRef = useRef<HTMLDivElement | null>(null);
  const userMenuContainerRef = useRef<HTMLDivElement | null>(null);

  const pathname = location.pathname;
  const runIdMatch = pathname.startsWith('/runs/') ? pathname.split('/')[2] : null;
  const adminTab = pathname === '/admin' ? (new URLSearchParams(location.search).get('tab') || 'users') : undefined;

  useEffect(() => {
    if (!runIdMatch || !user) {
      setRunName(null);
      return;
    }
    let cancelled = false;
    api.get<{ name?: string }>(`/runs/${runIdMatch}`).then(({ data }) => {
      if (!cancelled && data.name) setRunName(data.name);
    }).catch(() => {
      if (!cancelled) setRunName(null);
    });
    return () => { cancelled = true; };
  }, [runIdMatch, user]);

  useEffect(() => {
    if (isTourRunning) {
      setShowUserMenu(false);
      setShowNotifications(false);
    }
  }, [isTourRunning]);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const { count } = await notificationsAPI.getUnreadCount();
      setUnreadCount(count);
    } catch {
      setUnreadCount(0);
    }
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setNotificationsLoading(true);
    try {
      const list = await notificationsAPI.getList(false, 0, 50);
      setNotifications(Array.isArray(list) ? list : []);
      const { count } = await notificationsAPI.getUnreadCount();
      setUnreadCount(count);
    } catch {
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading || !user) return;
    fetchUnreadCount();
  }, [authLoading, user, fetchUnreadCount]);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const base = process.env.REACT_APP_API_URL || '';
    if (!base) return;
    const url = `${base}/notifications/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    es.addEventListener('new', () => {
      fetchUnreadCount();
      if (showNotificationsRef.current) fetchNotifications();
    });
    return () => es.close();
  }, [user, fetchUnreadCount, fetchNotifications]);

  useEffect(() => {
    if (showNotifications && user) fetchNotifications();
  }, [showNotifications, user, fetchNotifications]);

  useEffect(() => {
    if (!showNotifications && !showUserMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inNotifications = notificationsContainerRef.current?.contains(target);
      const inUserMenu = userMenuContainerRef.current?.contains(target);
      if (!inNotifications && !inUserMenu) {
        setShowNotifications(false);
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications, showUserMenu]);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await notificationsAPI.markAllRead();
      track(AnalyticsEvents.NOTIFICATION_READ_ALL);
      setUnreadCount(0);
      const list = await notificationsAPI.getList(false, 0, 50);
      setNotifications(Array.isArray(list) ? list : []);
    } catch {
      // ignore
    }
  }, []);

  const handleDeleteNotification = useCallback(async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    try {
      await notificationsAPI.delete(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      const { count } = await notificationsAPI.getUnreadCount();
      setUnreadCount(count);
    } catch {
      // ignore
    }
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/');
  }, [logout, navigate]);

  const breadcrumbs = getHeaderBreadcrumbs(pathname, {
    ...(runName != null ? { runName } : {}),
    ...(adminTab != null ? { adminTab } : {}),
  });
  const dropdownZIndex = isTourRunning ? 1 : 50;

  return (
    <header className="bg-gray-900/95 backdrop-blur-md shadow-lg border-b border-white/20 fixed top-0 right-0 left-0 lg:left-[var(--sidebar-w,14rem)] z-20 h-16">
      <div className="flex justify-between items-center h-full px-4 lg:px-6">
        <div className="flex items-center space-x-2 lg:space-x-4">
          <button
            onClick={() => setIsOpen(true)}
            className="lg:hidden p-2 rounded-md text-white hover:text-cyan-400 hover:bg-white/10 transition-colors touch-manipulation"
            aria-label="Open navigation menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          <nav className="flex items-center space-x-1 text-sm overflow-hidden">
            {breadcrumbs.map((crumb, index) => (
              <div key={crumb.key} className="flex items-center flex-shrink-0">
                {index === 0 && <Home className="w-4 h-4 text-gray-300 mr-1 flex-shrink-0" />}
                {index > 0 && <ChevronRight className="w-3 h-3 lg:w-4 lg:h-4 text-gray-400 mx-1 flex-shrink-0" />}
                {index === breadcrumbs.length - 1 && crumb.key !== 'home' ? (
                  // Last crumb (current page) is non-interactive text — except
                  // when Home is the only crumb, which happens for routes that
                  // aren't registered in getHeaderBreadcrumbs. Keeping Home
                  // clickable everywhere lets users navigate back to the
                  // dashboard regardless of whether the current page is in
                  // the breadcrumb table.
                  <span className="text-white font-medium truncate max-w-32 lg:max-w-none">{crumb.label}</span>
                ) : (
                  <button
                    onClick={() => navigate(crumb.path)}
                    className="text-gray-300 hover:text-white transition-colors truncate max-w-20 lg:max-w-none"
                  >
                    {crumb.label}
                  </button>
                )}
              </div>
            ))}
          </nav>
        </div>
        <div className="flex items-center space-x-2 lg:space-x-4">
          <HeaderNotifications
            ref={notificationsContainerRef}
            show={showNotifications}
            // Toggling either popup closes the other so they're mutually
            // exclusive — clicking the user icon while notifications is
            // open should switch panels, not stack them.
            onToggle={() => {
              setShowNotifications((prev) => !prev);
              setShowUserMenu(false);
            }}
            notifications={notifications}
            loading={notificationsLoading}
            unreadCount={unreadCount}
            onMarkAllRead={handleMarkAllRead}
            onDelete={handleDeleteNotification}
            formatDateTime={formatNotificationDateTime}
            zIndex={dropdownZIndex}
          />
          <HeaderUserMenu
            ref={userMenuContainerRef}
            show={showUserMenu}
            onToggle={() => {
              setShowUserMenu((prev) => !prev);
              setShowNotifications(false);
            }}
            user={user}
            onNavigate={(path) => navigate(path)}
            onStartTour={startTour}
            onLogout={handleLogout}
            pathname={location.pathname}
            zIndex={dropdownZIndex}
          />
        </div>
      </div>
    </header>
  );
};

export default Header;
